import { Download, Award } from "lucide-react";
import { SAILOR } from "@/data/ships";
import useReveal from "@/hooks/useReveal";

export default function ServiceRecord() {
  const ref = useReveal();

  return (
    <section
      id="service-record"
      ref={ref}
      className="reveal relative z-10 py-16 sm:py-24 md:py-32 border-t border-[var(--c-border)] bg-[var(--c-bg-elev-2)] scroll-mt-16 sm:scroll-mt-20"
      data-testid="service-record-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="overline mb-3 sm:mb-4">Your Service Record</p>
            <h2 className="font-serif-display text-3xl xs:text-4xl md:text-5xl text-[var(--c-text)] tracking-tight">
              The Full Record
            </h2>
            <div className="hairline my-6 sm:my-8 max-w-xs" />
            <p className="text-[var(--c-text-muted)] text-sm sm:text-base md:text-lg leading-relaxed">
              All eleven drafts, arranged in the manner of the official
              certificate. A printable PDF is below — feel free to print it
              and keep it with the rest of the papers.
            </p>

            <a
              href="/service-record.pdf"
              download="TP-Field-Service-Record.pdf"
              className="mt-8 sm:mt-10 group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[var(--c-accent)] hover:bg-[var(--c-accent-bright)] active:bg-[var(--c-accent-bright)] text-[var(--c-bg)] font-mono-plex text-xs uppercase tracking-[0.22em] px-6 py-4 min-h-[52px] transition-colors"
              data-testid="download-record-btn"
            >
              <Download size={16} strokeWidth={2.4} />
              Download Service Record (PDF)
            </a>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-[var(--c-border)] bg-[var(--c-bg-elev)] p-5 sm:p-8 md:p-10">
              <div className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-6 pb-5 sm:pb-6 border-b border-[var(--c-border)]">
                <span className="w-10 h-10 sm:w-12 sm:h-12 border border-[var(--c-accent)]/50 flex items-center justify-center text-[var(--c-accent)] shrink-0">
                  <Award size={20} strokeWidth={1.8} />
                </span>
                <div className="min-w-0">
                  <p className="overline mb-1">Final Rate</p>
                  <p className="font-serif-display text-lg sm:text-xl md:text-2xl text-[var(--c-text)] leading-snug">
                    {SAILOR.finalRank}
                  </p>
                  <p className="text-xs sm:text-sm text-[var(--c-text-muted)] mt-1 italic">
                    {SAILOR.branch}
                  </p>
                </div>
              </div>

              <dl className="grid grid-cols-2 gap-5 sm:gap-6">
                <Row label="Full Name" value={SAILOR.fullName} />
                <Row label="Date of Birth" value={SAILOR.born} />
                <Row label="Service" value={SAILOR.serviceYears} />
                <Row label="Total Drafts" value="11" />
              </dl>

              <p className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-[var(--c-border)] text-xs sm:text-sm text-[var(--c-text-muted)] leading-relaxed italic">
                Sixteen years of continuous service, Dad — from a sixteen-year-old
                boy entrant to one of the most senior technical ratings in the
                Royal Navy. A Mechanician First Class qualified to take charge
                of a watch in the manoeuvring room of a nuclear-armed ballistic
                missile submarine on continuous-at-sea deterrent patrol.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="min-w-0">
      <dt className="overline mb-1">{label}</dt>
      <dd className="font-serif-display text-sm sm:text-base md:text-lg text-[var(--c-text)] leading-snug break-words">
        {value}
      </dd>
    </div>
  );
}
